import * as XLSX from 'xlsx';
import {
  Epic,
  ParsedBacklog,
  StandardTask,
  STANDARD_TASKS,
  UserStory,
  shouldHaveStandardTasks,
  parsePriorityAndSequence
} from '../types/story';
import { buildHuId, normalizeStoryId } from './storyIds';

export type ExcelImportMode = 'replace' | 'append' | 'update_by_id' | 'first_import';

export interface ParsedExcelBacklog {
  filename: string;
  stories: UserStory[];
  executive_summary?: string;
}

export interface ExcelImportStats {
  mode: ExcelImportMode;
  imported: number;
  added: number;
  updated: number;
}

export interface AppliedExcelImport {
  backlog: ParsedBacklog;
  stats: ExcelImportStats;
}

const STORIES_SHEET = 'Historias de Usuario';
const SUMMARY_SHEET = 'Resumen Ejecutivo';

const normalizeHeader = (value: string): string => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();
};

const normalizeCellText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
};

const parseStatus = (value: string): UserStory['status'] => {
  const normalized = value.toLowerCase();
  if (normalized.includes('review') || normalized.includes('revision') || normalized.includes('revisión')) {
    return 'review';
  }
  if (normalized.includes('approved') || normalized.includes('aprobada') || normalized.includes('aprobado')) {
    return 'approved';
  }
  return 'draft';
};

const parseStoryPoints = (value: string): number => {
  const match = value.match(/(\d+)/);
  if (!match) return 3;
  const points = Number(match[1]);
  if ([1, 2, 3, 5, 8, 13].includes(points)) return points;
  if (points <= 1) return 1;
  if (points <= 2) return 2;
  if (points <= 4) return 3;
  if (points <= 6) return 5;
  if (points <= 10) return 8;
  return 13;
};

const parseListFromCell = (value: string): string[] => {
  if (!value) return [];

  const cleaned = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\\n/g, '\n');

  const unique = new Set<string>();
  cleaned.split('\n').forEach((line) => {
    const normalized = line
      .trim()
      .replace(/^([-*•]\s*)+/, '')
      .replace(/^\d+[.)]\s*/, '')
      .trim();
    if (normalized) {
      unique.add(normalized);
    }
  });

  return Array.from(unique);
};

const extractLinksFromCell = (value: string): string[] => {
  if (!value) return [];
  const links: string[] = [];
  const matches = value.matchAll(/https?:\/\/[^\s<>")\]]+/gi);
  for (const match of matches) {
    links.push(match[0]);
  }
  if (links.length > 0) {
    return Array.from(new Set(links));
  }
  return parseListFromCell(value);
};

const parseTaskType = (title: string): StandardTask['type'] => {
  const normalized = title.toLowerCase();
  if (normalized.includes('context')) return 'Contextualización';
  if (normalized.includes('prueb')) return 'Pruebas';
  if (normalized.includes('desplieg') || normalized.includes('produccion') || normalized.includes('producción')) {
    return 'Despliegue';
  }
  return 'Desarrollo';
};

const parseTasks = (value: string, rol: string): StandardTask[] => {
  const lines = parseListFromCell(value);
  if (lines.length === 0) {
    return shouldHaveStandardTasks(rol) ? [...STANDARD_TASKS] : [];
  }

  const tasks: StandardTask[] = [];
  lines.forEach((line) => {
    const match = line.match(/(.+?)\s*\((\d+(?:[.,]\d+)?)h\)\s*$/i);
    const title = (match?.[1] || line).trim();
    const hours = match?.[2] ? Math.max(1, Math.round(Number(match[2].replace(',', '.')) || 1)) : 1;
    if (!title) return;
    tasks.push({
      title,
      description: '',
      type: parseTaskType(title),
      estimated_hours: hours,
    });
  });

  return tasks;
};

const getRowValue = (row: Record<string, unknown>, aliases: string[]): string => {
  const entries = Object.entries(row);
  for (const alias of aliases) {
    const match = entries.find(([key]) => normalizeHeader(key) === alias);
    if (match) {
      return normalizeCellText(match[1]);
    }
  }
  return '';
};

const buildStoryFromRow = (row: Record<string, unknown>, fallbackIndex: number): UserStory | null => {
  const title = getRowValue(row, ['titulo', 'title']);
  const userStory = getRowValue(row, ['historiadeusuario', 'userstory', 'userstorytext']);
  const rol = getRowValue(row, ['rol', 'role']) || 'Usuario';

  if (!title && !userStory) {
    return null;
  }

  const rawId = getRowValue(row, ['id']);
  const storyId = normalizeStoryId(rawId) || buildHuId(fallbackIndex + 1);
  const criteriosFuncionales = parseListFromCell(getRowValue(row, ['criteriosfuncionales', 'criteriofuncional']));
  const criteriosTecnicos = parseListFromCell(getRowValue(row, ['criteriostecnicos', 'criteriotecnico']));
  const priorityData = parsePriorityAndSequence(getRowValue(row, ['prioridad', 'priority']));

  return {
    id: storyId,
    title: title || `Historia ${fallbackIndex + 1}`,
    user_story: userStory,
    rol,
    criterios_funcionales: criteriosFuncionales,
    criterios_tecnicos: criteriosTecnicos,
    figma_links: extractLinksFromCell(getRowValue(row, ['enlacesfigma', 'figma', 'figmalink'])),
    insumos: parseListFromCell(getRowValue(row, ['insumos', 'insumo'])),
    story_points: parseStoryPoints(getRowValue(row, ['storypoints', 'storypoint', 'puntos'])),
    priority: priorityData.priority,
    priority_sequence: priorityData.priority_sequence,
    epic: getRowValue(row, ['epica', 'epic']) || undefined,
    status: parseStatus(getRowValue(row, ['estado', 'status'])),
    tasks: parseTasks(getRowValue(row, ['tareas', 'task', 'tasks']), rol),
  };
};

const createIdAllocator = (seedStories: Array<Pick<UserStory, 'id'>>) => {
  const used = new Set<string>();
  let nextHu = 1;

  seedStories.forEach((story) => {
    const normalized = normalizeStoryId(story.id);
    if (!normalized) return;
    used.add(normalized);
    const huMatch = normalized.match(/^HU-(\d+)$/i);
    if (huMatch) {
      nextHu = Math.max(nextHu, Number(huMatch[1]) + 1);
    }
  });

  return (preferredId?: string): string => {
    let candidate = normalizeStoryId(preferredId) || buildHuId(nextHu++);
    while (used.has(candidate)) {
      candidate = buildHuId(nextHu++);
    }
    used.add(candidate);
    const huMatch = candidate.match(/^HU-(\d+)$/i);
    if (huMatch) {
      nextHu = Math.max(nextHu, Number(huMatch[1]) + 1);
    }
    return candidate;
  };
};

const rebuildEpics = (stories: UserStory[], existingEpics: Epic[]): Epic[] => {
  const descriptions = new Map<string, string>();
  existingEpics.forEach((epic) => {
    descriptions.set(epic.title.trim().toLowerCase(), epic.description || '');
  });

  const map = new Map<string, Epic>();
  let sequence = 1;

  stories.forEach((story) => {
    const epicTitle = (story.epic || '').trim();
    if (!epicTitle) return;
    const key = epicTitle.toLowerCase();
    if (!map.has(key)) {
      map.set(key, {
        id: `epic-${String(sequence++).padStart(3, '0')}`,
        title: epicTitle,
        description: descriptions.get(key) || '',
        stories: [],
      });
    }
    map.get(key)!.stories.push(story);
  });

  return Array.from(map.values());
};

const createBaseBacklog = (currentBacklog: ParsedBacklog | null): ParsedBacklog => {
  if (currentBacklog) return currentBacklog;
  return {
    executive_summary: '',
    business_rules: [],
    epics: [],
    stories: [],
    raw_markdown: '',
  };
};

const findByNormalizedId = (stories: UserStory[]): Map<string, number> => {
  const map = new Map<string, number>();
  stories.forEach((story, index) => {
    const normalized = normalizeStoryId(story.id);
    if (normalized) map.set(normalized, index);
  });
  return map;
};

const sanitizeImportedStories = (stories: UserStory[]): UserStory[] => {
  const allocate = createIdAllocator([]);
  return stories.map((story) => ({
    ...story,
    id: allocate(story.id),
  }));
};

const dedupeImportedStoriesById = (stories: UserStory[]): UserStory[] => {
  const deduped: UserStory[] = [];
  const seen = new Set<string>();

  // Keep the last occurrence for duplicated HU IDs in the same file
  for (let index = stories.length - 1; index >= 0; index -= 1) {
    const story = stories[index];
    const normalizedId = normalizeStoryId(story.id);
    const huKey = normalizedId && /^HU-\d+$/i.test(normalizedId) ? normalizedId : null;

    if (huKey) {
      if (seen.has(huKey)) continue;
      seen.add(huKey);
      deduped.unshift({
        ...story,
        id: huKey,
      });
      continue;
    }

    deduped.unshift(story);
  }

  return deduped;
};

const parseExecutiveSummary = (workbook: XLSX.WorkBook): string | undefined => {
  const summarySheet = workbook.Sheets[SUMMARY_SHEET];
  if (!summarySheet) return undefined;

  const explicit = normalizeCellText(summarySheet['A3']?.v);
  if (explicit) return explicit;

  const matrix = XLSX.utils.sheet_to_json(summarySheet, { header: 1, raw: false }) as unknown[][];
  for (const row of matrix) {
    for (const cell of row) {
      const content = normalizeCellText(cell);
      if (content && content.toLowerCase() !== 'resumen ejecutivo') {
        return content;
      }
    }
  }
  return undefined;
};

export const parseExcelBacklogFile = async (file: File): Promise<ParsedExcelBacklog> => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const storiesSheetName = workbook.SheetNames.find((name) => name === STORIES_SHEET) || workbook.SheetNames[0];
  const storiesSheet = storiesSheetName ? workbook.Sheets[storiesSheetName] : null;

  if (!storiesSheet) {
    throw new Error('El archivo Excel no contiene una hoja de historias válida.');
  }

  const rows = XLSX.utils.sheet_to_json(storiesSheet, { defval: '', raw: false }) as Record<string, unknown>[];
  const parsedStories: UserStory[] = [];
  rows.forEach((row, index) => {
    const story = buildStoryFromRow(row, index);
    if (story) parsedStories.push(story);
  });

  if (parsedStories.length === 0) {
    throw new Error('No se encontraron historias en la hoja "Historias de Usuario".');
  }

  const dedupedStories = dedupeImportedStoriesById(parsedStories);

  return {
    filename: file.name,
    stories: sanitizeImportedStories(dedupedStories),
    executive_summary: parseExecutiveSummary(workbook),
  };
};

export const applyExcelImportToBacklog = (
  currentBacklog: ParsedBacklog | null,
  parsedExcel: ParsedExcelBacklog,
  mode: ExcelImportMode
): AppliedExcelImport => {
  const base = createBaseBacklog(currentBacklog);
  const importedStories = sanitizeImportedStories(dedupeImportedStoriesById(parsedExcel.stories));
  const resolvedMode: ExcelImportMode = mode === 'first_import' ? 'replace' : mode;

  let finalStories: UserStory[] = [];
  let added = 0;
  let updated = 0;

  if (resolvedMode === 'replace') {
    finalStories = sanitizeImportedStories(importedStories);
    added = finalStories.length;
  } else if (resolvedMode === 'append') {
    const allocate = createIdAllocator(base.stories);
    const appended = importedStories.map((story) => ({
      ...story,
      id: allocate(story.id),
    }));
    finalStories = [...base.stories, ...appended];
    added = appended.length;
  } else {
    const existingStories = base.stories.map((story, index) => {
      const normalized = normalizeStoryId(story.id);
      return {
        ...story,
        id: normalized && /^HU-\d+$/i.test(normalized) ? normalized : (story.id || buildHuId(index + 1)),
      };
    });
    const matchIndex = findByNormalizedId(existingStories);
    const newStories = [...existingStories];
    const appendQueue: UserStory[] = [];
    const appendAllocate = createIdAllocator(existingStories);

    importedStories.forEach((story) => {
      const key = normalizeStoryId(story.id);
      if (key && matchIndex.has(key)) {
        const index = matchIndex.get(key)!;
        newStories[index] = {
          ...story,
          id: key,
        };
        updated += 1;
      } else {
        appendQueue.push({
          ...story,
          id: appendAllocate(story.id),
        });
      }
    });

    finalStories = [...newStories, ...appendQueue];
    added = appendQueue.length;
  }

  const finalBacklog: ParsedBacklog = {
    ...base,
    executive_summary: parsedExcel.executive_summary || base.executive_summary || '',
    stories: finalStories,
    epics: rebuildEpics(finalStories, base.epics || []),
  };

  return {
    backlog: finalBacklog,
    stats: {
      mode,
      imported: importedStories.length,
      added,
      updated,
    },
  };
};
